// src/components/unsubscribe/UnsubscribePage.js
import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Button, 
  CircularProgress,
  Card,
  Link,
  useTheme
} from '@mui/material';
import { CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from '../../config/axiosConfig';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const UnsubscribePage = () => {
  const theme = useTheme();
  const query = useQuery();
  const navigate = useNavigate();
  
  const email = query.get('email');
  const token = query.get('token');
  
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState('');
  
  useEffect(() => {
    if (!email || !token) {
      setLoading(false);
      setSuccess(false);
      setMessage('Cannot process your unsubscribe request.');
      return;
    }
    
    const processUnsubscribe = async () => {
      try {
        const response = await axios.post('/api/unsubscribe', { email, token });
        setSuccess(true);
        setMessage(response.data.message || 'You have been successfully unsubscribed from marketing emails.');
      } catch (error) {
        setSuccess(false);
        setMessage(
          error.response?.data?.message || 
          'An error occurred while processing your request. Please try again later.'
        );
      } finally {
        setLoading(false);
      }
    };
    
    processUnsubscribe();
  }, [email, token]);
  
  const handleGoHome = () => {
    navigate('/');
  };
  
  const handleGoToProfile = () => {
    navigate('/profile');
  };
  
  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Card 
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 2,
            backgroundColor: 'rgba(19, 47, 76, 0.4)',
            textAlign: 'center'
          }}
        >
          <CircularProgress color="primary" sx={{ my: 4 }} />
          <Typography>Processing your request...</Typography>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      <Card
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          backgroundColor: 'rgba(19, 47, 76, 0.4)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          {success ? (
            <CheckCircle 
              sx={{ 
                fontSize: 48, 
                color: theme.palette.positive.main,
                mb: 1
              }} 
            />
          ) : (
            <ErrorIcon 
              sx={{ 
                fontSize: 48, 
                color: theme.palette.negative.main,
                mb: 1
              }} 
            />
          )}
          
          <Typography 
            variant="h5" 
            component="h2"
            sx={{ 
              fontWeight: 700,
              mb: 1,
            }}
          >
            {success ? 'Successfully Unsubscribed' : 'Something Went Wrong'}
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2 }}>
            {message}
          </Typography>
          
          {success ? (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                You won't receive marketing emails from us anymore.
              </Typography>
              
              <Box sx={{ mt: 2 }}>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={handleGoToProfile}
                  sx={{ mr: 2, textTransform: 'none' }}
                >
                  Go to Profile
                </Button>
                <Button 
                  variant="outlined" 
                  onClick={handleGoHome}
                  sx={{ textTransform: 'none' }}
                >
                  Return to Dashboard
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                If you continue to have problems, please contact our support team.
              </Typography>
              
              <Box sx={{ mt: 2 }}>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={handleGoHome}
                  sx={{ mr: 2, textTransform: 'none' }}
                >
                  Return to Dashboard
                </Button>
                <Link href="mailto:support@marketrodeo.com" sx={{ textDecoration: 'none' }}>
                  <Button 
                    variant="outlined"
                    sx={{ textTransform: 'none' }}
                  >
                    Contact Support
                  </Button>
                </Link>
              </Box>
            </>
          )}
        </Box>
      </Card>
    </Container>
  );
};

export default UnsubscribePage;